"use client"

import { createContext, useContext, useEffect, useRef, useState } from "react"
import { Empresa } from "@/interfaces/empresa"
import { decryptObject, encryptObject } from "@/utils/sign-object"
import { useMutation } from "@tanstack/react-query"
import internal from "@/service/internal"
import LoadingPage from "../ui/LoadingPage"
import { usePathname } from "next/navigation"
import posthog from "posthog-js"
import localStorage from "@/utils/localStorage"

type AdminContextType = Empresa

export const AdminContext = createContext<AdminContextType | null>(null)

type AdminProviderProps = {
  children: React.ReactNode
  slug: string
}

const DEFAULT_EMPRESA = {
  id: 0,
  nombre: "",
  slug: "",
  nit: "",
  nrc: "",
  representante_nit: "",
  representante_nombre: "",
  cod_actividad: "",
  nombre_actividad: "",
  tipo_establecimiento: "",
  direccion_complemento: "",
  departamento: "",
  municipio: "",
  telefono: "",
  email: "",
  correlativo_fc: "",
  ambiente: "",
  correlativo_ccf: "",
  enabled: false,
  nombre_comercial: "",
  correlativo_anul: "",
  correlativo_cont: ""
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children, slug }) => {
  const [data, setData] = useState<Empresa>(DEFAULT_EMPRESA)
  let loaded = useRef(false)
  const pathname = usePathname()

  // Detect if we're in a patient-facing route
  const isPatientRoute = pathname?.includes("/pacientes/") && !pathname?.includes("/admin/")

  const { mutate: fetchData, isPending } = useMutation({
    mutationKey: ["empresa", slug, isPatientRoute],
    mutationFn: (empresa_slug: string) => {
      // use public endpoint for minimal data
      return internal.get(`/api/public/empresa?empresa_slug=${empresa_slug}`)
    },
    onSuccess: (data) => {
      if (data?.data?.id) {
        try {
          const signed = encryptObject(data?.data, process.env.STORAGE_SECRET ?? "")
          if (typeof window !== "undefined" && window.isSecureContext) {
            try {
              localStorage.setItem("empresa", signed)
            } catch (storageError) {
              console.warn("[AdminProvider] localStorage.setItem failed:", storageError)
            }
          }
          if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
            try {
              posthog.setPersonProperties({ slug, isPatientRoute })
            } catch (posthogError) {
              console.warn("[AdminProvider] posthog.setPersonProperties failed:", posthogError)
            }
          }
        } catch (error) {
          console.warn("[AdminProvider] onSuccess failed:", error)
        }
      }
    },
    onSettled: () => {
      loaded.current = true
    }
    // onError: (error: any) => {
    //   console.log("[AdminProvider] onError failed:", error)
    //   if(error.response.status === 401 && slug) {}
    //     router.push(paths.admin(slug).login)
    // }
  })

  useEffect(() => {
    if (typeof window === "undefined" || isPending) return
    let empresa: string | null = null
    try {
      if (window.isSecureContext) {
        empresa = localStorage.getItem("empresa")
      }
    } catch (storageError) {
      console.warn("[AdminProvider] localStorage.getItem failed:", storageError)
      setData(DEFAULT_EMPRESA)
      return
    }
    try {
      const storageData = empresa ? decryptObject(empresa, process.env.STORAGE_SECRET ?? "") : null
      if (storageData?.slug === slug) {
        setData(storageData)
      } else {
        if (window.isSecureContext) {
          try {
            localStorage.removeItem("empresa")
          } catch (e) {
            // ignore
          }
        }
        setData(DEFAULT_EMPRESA)
      }
    } catch (error) {
      console.error("[AdminProvider] decrypt failed:", error)
      setData(DEFAULT_EMPRESA)
    }
  }, [slug, isPending])

  useEffect(() => {
    fetchData(slug)
  }, [slug, isPatientRoute])

  return (
    <AdminContext.Provider value={data}>
      {isPending || !loaded.current ? <LoadingPage /> : children}
    </AdminContext.Provider>
  )
}

export const useAdmin = () => {
  const context = useContext(AdminContext)

  const error = new Error("useAdmin must be used within a AdminProvider")

  if (context === null) {
    throw new Error("useAdmin must be used within a AdminProvider")
  }

  return context
}
